<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <div class="flex items-end justify-between gap-3">
    <div>
      <p class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Shopee · Manifest</p>
      <h1 class="text-2xl font-semibold tracking-tight text-foreground">Customs Manifest Generator</h1>
      <p class="text-sm text-muted-foreground mt-1">Generate XML manifest files for customs submission</p>
    </div>
    <button type="button"
      class="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-border-strong bg-surface text-sm text-foreground hover:bg-surface-raised transition shrink-0"
      (click)="resetForm()">
      <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
      Reset Form
    </button>
  </div>

  <!-- Status messages -->
  @if (successMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-500/15 dark:border-green-500/30 p-3 text-sm text-green-800 dark:text-green-300">
      <svg xmlns="http://www.w3.org/2000/svg" class="size-4 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      <span class="flex-1">{{ successMessage }}</span>
      <button type="button" class="appearance-none border-0 bg-transparent p-0 ml-auto focus-visible:ring-2 focus-visible:ring-accent rounded" (click)="clearMessages()" aria-label="Dismiss">
        <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  }
  @if (errorMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/15 dark:border-red-500/30 p-3 text-sm text-red-800 dark:text-red-300">
      <svg xmlns="http://www.w3.org/2000/svg" class="size-4 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
      <span class="flex-1">{{ errorMessage }}</span>
      <button type="button" class="appearance-none border-0 bg-transparent p-0 ml-auto focus-visible:ring-2 focus-visible:ring-accent rounded" (click)="clearMessages()" aria-label="Dismiss">
        <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  }

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

    <!-- Left column: Form (col-span-2) -->
    <div class="lg:col-span-2 space-y-5">
      <form [formGroup]="manifestForm" (ngSubmit)="generateManifest()">

        <!-- Pre-Alert Selection Card -->
        <div hlmCard class="mb-5">
          <div class="px-4 py-3 border-b border-border">
            <h2 class="text-sm font-semibold text-foreground">Pre-Alert Selection</h2>
          </div>
          <div class="p-4">
            <div class="max-w-sm">
              <label class="block text-xs font-medium text-muted-foreground mb-2">Pre-Alert <span class="text-red-500">*</span></label>
              <div class="relative">
                <select formControlName="preAlertId" (change)="onPreAlertSelected()"
                  class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none"
                  [class.border-red-400]="hasFieldError('preAlertId')">
                  <option value="">Select a pre-alert…</option>
                  @for (preAlert of preAlerts; track preAlert.ladingBill) {
                    <option [value]="preAlert.ladingBill">
                      {{ preAlert.ladingBill }} - {{ preAlert.transportCompany }} ({{ preAlert.status }})
                    </option>
                  }
                </select>
                <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
              @if (hasFieldError('preAlertId')) {
                <p class="mt-1 text-xs text-red-500">{{ getFieldError('preAlertId') }}</p>
              }
            </div>
          </div>
        </div>

        <!-- Registry & Transport Info Card -->
        <div hlmCard class="mb-5">
          <div class="px-4 py-3 border-b border-border">
            <h2 class="text-sm font-semibold text-foreground">Registry &amp; Transport Information</h2>
          </div>
          <div class="p-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <!-- Registry Number -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Registry Number <span class="text-red-500">*</span></label>
                <input type="text" formControlName="registryNumber"
                  [class.border-red-400]="hasFieldError('registryNumber')"
                  placeholder="Enter registry number"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('registryNumber')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('registryNumber') }}</p>
                }
              </div>

              <!-- Bill of Lading Reference -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Bill of Lading Reference <span class="text-red-500">*</span></label>
                <input type="text" formControlName="billOfLadingReference"
                  [class.border-red-400]="hasFieldError('billOfLadingReference')"
                  placeholder="Enter bill of lading reference"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('billOfLadingReference')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('billOfLadingReference') }}</p>
                }
              </div>

              <!-- Master AWB -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Master AWB</label>
                <input type="text" formControlName="masterAwb"
                  placeholder="Enter master airwaybill"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
              </div>

              <!-- BL Number -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">BL Number</label>
                <input type="text" formControlName="blNumber"
                  placeholder="Default: 1"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                <p class="mt-1 text-[11px] text-muted-foreground">Default value is "1"</p>
              </div>

              <!-- Transport Mode -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Transport Mode</label>
                <input type="text" formControlName="transportMode"
                  placeholder="Default: 4"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                <p class="mt-1 text-[11px] text-muted-foreground">Default value is "4"</p>
              </div>

              <!-- Port of Loading -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Port of Loading</label>
                <input type="text" formControlName="placeOfLoading"
                  placeholder="Auto-populated from pre-alert"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                <p class="mt-1 text-[11px] text-muted-foreground">Automatically filled from selected pre-alert</p>
              </div>

              <!-- Port of Unloading -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Port of Unloading</label>
                <input type="text" formControlName="placeOfUnloading"
                  placeholder="Auto-populated from pre-alert"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                <p class="mt-1 text-[11px] text-muted-foreground">Automatically filled from selected pre-alert</p>
              </div>

              <!-- B/L Type -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">B/L Type <span class="text-red-500">*</span></label>
                <div class="relative">
                  <select formControlName="blType"
                    [class.border-red-400]="hasFieldError('blType')"
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none">
                    @for (option of blTypeOptions; track option.value) {
                      <option [value]="option.value">{{ option.label }}</option>
                    }
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                @if (hasFieldError('blType')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('blType') }}</p>
                }
              </div>

              <!-- Nature -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Nature <span class="text-red-500">*</span></label>
                <input type="text" formControlName="nature"
                  [class.border-red-400]="hasFieldError('nature')"
                  placeholder="Enter nature of goods"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('nature')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('nature') }}</p>
                }
              </div>

              <!-- Customs Office Code -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Customs Office Code <span class="text-red-500">*</span></label>
                <div class="relative">
                  <select formControlName="customsOfficeCode"
                    [class.border-red-400]="hasFieldError('customsOfficeCode')"
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none">
                    @for (code of customsOfficeOptions; track code.code) {
                      <option [value]="code.code">{{ code.code }} - {{ code.name }}</option>
                    }
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                @if (hasFieldError('customsOfficeCode')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('customsOfficeCode') }}</p>
                }
              </div>

              <!-- Date of Arrival -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Date of Arrival <span class="text-red-500">*</span></label>
                <input type="date" formControlName="dateOfArrival"
                  [class.border-red-400]="hasFieldError('dateOfArrival')"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('dateOfArrival')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('dateOfArrival') }}</p>
                }
              </div>

              <!-- Time of Arrival -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Time of Arrival <span class="text-red-500">*</span></label>
                <input type="time" formControlName="timeOfArrival"
                  [class.border-red-400]="hasFieldError('timeOfArrival')"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('timeOfArrival')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('timeOfArrival') }}</p>
                }
              </div>

              <!-- Gross Weight -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Gross Weight (kg) <span class="text-red-500">*</span></label>
                <input type="number" formControlName="grossWeight"
                  [class.border-red-400]="hasFieldError('grossWeight')"
                  placeholder="Enter gross weight"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('grossWeight')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('grossWeight') }}</p>
                }
              </div>

              <!-- Number of Packages -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Number of Packages</label>
                <input type="number" formControlName="numberOfPackages"
                  placeholder="Enter number of packages"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
              </div>

              <!-- Carrier Code -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Carrier Code <span class="text-red-500">*</span></label>
                <div class="relative">
                  <select formControlName="carrierCode"
                    [class.border-red-400]="hasFieldError('carrierCode')"
                    class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none">
                    <option value="">Select carrier…</option>
                    @for (carrier of carrierOptions; track carrier.code) {
                      <option [value]="carrier.code">{{ carrier.code }} - {{ carrier.name }}</option>
                    }
                  </select>
                  <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                @if (hasFieldError('carrierCode')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('carrierCode') }}</p>
                }
              </div>

              <!-- Transport Identity -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Transport Identity</label>
                <input type="text" formControlName="transportIdentity"
                  placeholder="Enter transport identity"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
              </div>

              <!-- Nationality Code -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Nationality Code <span class="text-red-500">*</span></label>
                <input type="text" formControlName="nationalityCode"
                  [class.border-red-400]="hasFieldError('nationalityCode')"
                  placeholder="e.g., PH"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('nationalityCode')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('nationalityCode') }}</p>
                }
              </div>

              <!-- Nationality Name -->
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Nationality Name <span class="text-red-500">*</span></label>
                <input type="text" formControlName="nationalityName"
                  [class.border-red-400]="hasFieldError('nationalityName')"
                  placeholder="e.g., Philippines"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('nationalityName')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('nationalityName') }}</p>
                }
              </div>

            </div>
          </div>
        </div>

        <!-- Party Information Card -->
        <div hlmCard class="mb-5">
          <div class="px-4 py-3 border-b border-border">
            <h2 class="text-sm font-semibold text-foreground">Party Information</h2>
          </div>
          <div class="p-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Exporter / Shipper <span class="text-red-500">*</span></label>
                <textarea formControlName="exporterShipper"
                  [class.border-red-400]="hasFieldError('exporterShipper')"
                  rows="3" placeholder="Enter exporter/shipper details"
                  class="w-full border border-border-strong bg-surface rounded-md px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition resize-none"></textarea>
                @if (hasFieldError('exporterShipper')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('exporterShipper') }}</p>
                }
              </div>
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Exporter Address</label>
                <textarea formControlName="exporterAddress"
                  rows="3" placeholder="Enter exporter address"
                  class="w-full border border-border-strong bg-surface rounded-md px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition resize-none"></textarea>
              </div>
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Consignee <span class="text-red-500">*</span></label>
                <textarea formControlName="consignee"
                  [class.border-red-400]="hasFieldError('consignee')"
                  rows="3" placeholder="Enter consignee details"
                  class="w-full border border-border-strong bg-surface rounded-md px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition resize-none"></textarea>
                @if (hasFieldError('consignee')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('consignee') }}</p>
                }
              </div>
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Consignee Address</label>
                <textarea formControlName="consigneeAddress"
                  rows="3" placeholder="Enter consignee address"
                  class="w-full border border-border-strong bg-surface rounded-md px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition resize-none"></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Cargo Information Card -->
        <div hlmCard class="mb-5">
          <div class="px-4 py-3 border-b border-border">
            <h2 class="text-sm font-semibold text-foreground">Cargo Information</h2>
          </div>
          <div class="p-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Packages Codes <span class="text-red-500">*</span></label>
                <input type="text" formControlName="packagesCodes"
                  [class.border-red-400]="hasFieldError('packagesCodes')"
                  placeholder="Enter packages codes"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('packagesCodes')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('packagesCodes') }}</p>
                }
              </div>
              <div>
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Marks &amp; Numbers <span class="text-red-500">*</span></label>
                <input type="text" formControlName="marksAndNumbers"
                  [class.border-red-400]="hasFieldError('marksAndNumbers')"
                  placeholder="Enter marks and numbers"
                  class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                @if (hasFieldError('marksAndNumbers')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('marksAndNumbers') }}</p>
                }
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-muted-foreground mb-1.5">Description of Goods <span class="text-red-500">*</span></label>
                <textarea formControlName="descriptionOfGoods"
                  [class.border-red-400]="hasFieldError('descriptionOfGoods')"
                  rows="3" placeholder="Enter detailed description of goods"
                  class="w-full border border-border-strong bg-surface rounded-md px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition resize-none"></textarea>
                @if (hasFieldError('descriptionOfGoods')) {
                  <p class="mt-1 text-xs text-red-500">{{ getFieldError('descriptionOfGoods') }}</p>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="flex gap-2 mb-4">
          <button type="submit"
            class="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            [disabled]="isSubmitting">
            @if (isSubmitting) {
              <span class="spinner-border spinner-border-sm me-1"></span>
              Generating…
            } @else {
              <svg xmlns="http://www.w3.org/2000/svg" class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg>
              Generate Manifest
            }
          </button>
          <button type="button"
            class="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-border-strong bg-surface text-sm text-foreground hover:bg-surface-raised transition"
            (click)="resetForm()">
            <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            Reset
          </button>
        </div>

      </form>
    </div>

    <!-- Right column: Preview & Actions -->
    <div class="space-y-5">

      <!-- Pre-Alert Info Card -->
      @if (selectedPreAlert) {
        <div hlmCard>
          <div class="px-4 py-3 border-b border-border">
            <h2 class="text-sm font-semibold text-foreground">Pre-Alert Information</h2>
          </div>
          <div class="p-4 space-y-2.5">
            @for (row of [
              { label: 'Lading Bill', value: selectedPreAlert.ladingBill, mono: true },
              { label: 'ETA', value: (selectedPreAlert.eta | date:'medium') ?? '—', mono: false },
              { label: 'From', value: selectedPreAlert.departureLocation, mono: true },
              { label: 'To', value: selectedPreAlert.arrivalLocation, mono: true },
              { label: 'Pieces', value: '' + selectedPreAlert.pieces, mono: false },
              { label: 'Weight', value: selectedPreAlert.chargeableWeight + ' kg', mono: false },
              { label: 'Carrier', value: selectedPreAlert.transportCompany, mono: false }
            ]; track row.label) {
              <div class="flex justify-between items-baseline gap-4">
                <span class="text-[11px] uppercase tracking-wider text-muted-foreground shrink-0">{{ row.label }}</span>
                <span [class.font-mono]="row.mono" class="text-[12.5px] font-medium text-foreground text-right">{{ row.value }}</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- XML Template Selection -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">XML Template</h2>
        </div>
        <div class="p-4">
          @if (isLoadingTemplates) {
            <div class="flex items-center gap-2 text-sm text-muted-foreground">
              <div class="h-4 w-4 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
              <span>Loading templates…</span>
            </div>
          }
          @if (!isLoadingTemplates) {
            <label class="block text-xs font-medium text-muted-foreground mb-1.5">Select Template</label>
            <div class="relative mb-3">
              <select
                [value]="selectedTemplate?.id || ''"
                (change)="onTemplateChange($event)"
                class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none">
                <option value="">Choose a template…</option>
                @for (template of xmlTemplates; track template.id) {
                  <option [value]="template.id">{{ template.name }} ({{ template.provider }})</option>
                }
              </select>
              <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            @if (selectedTemplate) {
              <div>
                <p class="text-xs font-medium text-foreground mb-1">{{ selectedTemplate.description }}</p>
                <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30">
                  {{ selectedTemplate.provider }}
                </span>
                <p class="mt-2 text-[11px] text-muted-foreground">Created: {{ selectedTemplate.createdAt | date:'short' }}</p>
              </div>
            }
          }
        </div>
      </div>

      <!-- XML Preview -->
      @if (xmlPreview) {
        <div hlmCard>
          <div class="px-4 py-3 border-b border-border">
            <h2 class="text-sm font-semibold text-foreground">XML Preview</h2>
          </div>
          <div class="p-4">
            <pre class="font-mono text-[11px] text-foreground/80 bg-surface-raised dark:bg-stone-800/60 rounded-md p-4 overflow-x-auto max-h-80 border border-border leading-relaxed"><code>{{ xmlPreview }}</code></pre>
          </div>
        </div>
      }

      <!-- Download -->
      @if (manifestData) {
        <div hlmCard>
          <div class="px-4 py-3 border-b border-border">
            <h2 class="text-sm font-semibold text-foreground">Download XML File</h2>
          </div>
          <div class="p-4">
            <p class="text-xs text-muted-foreground mb-3">Generate and download the XML manifest file for customs submission.</p>
            <button type="button"
              class="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              [disabled]="!selectedTemplate || isGeneratingXml"
              (click)="generateXmlFile()">
              @if (isGeneratingXml) {
                <span class="spinner-border spinner-border-sm me-1"></span>
                Generating…
              } @else {
                <svg xmlns="http://www.w3.org/2000/svg" class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h2l2 5 2-8 1 3h2"/></svg>
                Download XML File
              }
            </button>
            @if (!selectedTemplate) {
              <div class="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/15 dark:border-amber-500/30 p-2 mt-2 text-xs text-amber-800 dark:text-amber-300">
                <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <span>Please select an XML template first.</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Instructions Card -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Instructions</h2>
        </div>
        <div class="p-4">
          <ol class="text-sm text-foreground space-y-1.5 list-decimal list-inside leading-relaxed mb-3">
            <li>Select a pre-alert from the dropdown</li>
            <li>Fill in all required fields</li>
            <li>Click "Generate Manifest" to create the data</li>
            <li>Download the XML file for customs submission</li>
          </ol>
          <div class="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-500/15 dark:border-blue-500/30 p-2 text-xs text-blue-800 dark:text-blue-300">
            <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <span>Auto-populated fields are taken from the selected pre-alert data.</span>
          </div>
        </div>
      </div>

    </div>
  </div>

</div>
